// 이메일 입력 규칙 — 형식 검사 + [중복 확인] 상태 판정(2026-09-29 사용자 요청).
//
// 쓰는 곳(이메일 = 로그인 ID 를 **새로 정하는** 세 화면): 관리자 [사용자 추가] · 수퍼유저 [조직 만들기] · 본인 [내 이메일 변경].
// 화면 부품은 `views/EmailCheckField.tsx`, 상태 훅은 `views/useEmailCheck.ts`. 이 파일은 의존성 없는 순수 규칙이라 단위 시험한다.
//
// - 형식은 **입력 단계에서** 막는다(틀리면 [중복 확인]·저장 버튼이 꺼진다). 최종 판정은 서버 `@IsEmail`(같은 문구).
// - 중복은 사용자가 **[중복 확인] 버튼**을 눌러 확인한다(사용자 결정 — 자동 조회 아님). 확인한 뒤 주소를 고치면 다시 확인해야 한다.
// - 이미 쓰이면 "이미 사용 중" 만 알린다 — **어디서(우리/다른 조직) 쓰이는지는 알리지 않는다**(사용자 결정).
// - 🔴 "사용 가능" 은 확인한 **그 순간**의 답이다 → 저장 때 서버가 409 를 줄 수 있다(동시 등록). 그 문구도 같다.

// `.ts` 확장자 = node 단위 시험(`node --test`)이 그대로 읽게(draw/ 와 같은 방식).
import { normalizeEmail } from './loginEmails.ts';

export { normalizeEmail };

/** 최대 길이 — 서버 `EMAIL_MAX_LENGTH` 와 같다(입력칸 maxLength). */
export const EMAIL_MAX_LENGTH = 254;

/**
 * 대강의 형식: `로컬@도메인.최상위`. 공백·`@` 두 개·도메인의 빈 마디(`a@b..c`, `a@.b`, `a@b.`)를 막는다.
 * 서버 `@IsEmail` 보다 느슨하다 — 여기서 통과해도 서버가 400 을 줄 수 있고, 그 문구도 `EMAIL_FORMAT_MESSAGE` 로 같다.
 */
export const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// ── 안내 문구(서버와 같은 말 — 저장 409·400 이 떠도 화면 문구가 바뀌지 않게) ──
export const EMAIL_FORMAT_MESSAGE = '이메일 형식이 아닙니다.';
export const EMAIL_TAKEN_MESSAGE = '이미 사용 중인 이메일입니다.';
export const EMAIL_MINE_MESSAGE = '지금 쓰고 있는 이메일입니다.';
export const EMAIL_AVAILABLE_MESSAGE = '사용할 수 있는 이메일입니다.';
export const EMAIL_CHECKING_MESSAGE = '확인 중…';
/** 확인 전 안내(형식은 맞음). */
export const EMAIL_UNCHECKED_MESSAGE = '[중복 확인]을 눌러 주세요.';
/**
 * 서버가 중복 확인을 모를 때(옛 서버 — 확인 경로 404). 저장을 막으면 옛 서버에 붙은 앱에서 사용자 추가가 아예 안 되므로
 * **저장을 허용**하고 최종 판정은 저장 때의 409 에 맡긴다(형식은 이미 통과).
 */
export const EMAIL_UNSUPPORTED_MESSAGE = '저장할 때 중복을 확인합니다.';
/** 저장을 눌렀는데 아직 확인이 안 됐을 때(버튼이 꺼져 있어 보통은 안 보인다 — Enter 등 다른 경로 방어). */
export const EMAIL_CHECK_REQUIRED_MESSAGE = '이메일 [중복 확인]을 먼저 해 주세요.';

/** 형식이 맞는가(앞뒤 공백은 무시). */
export function isEmailFormat(raw: string): boolean {
  const e = raw.trim();
  return e.length > 0 && e.length <= EMAIL_MAX_LENGTH && EMAIL_FORMAT_RE.test(e);
}

/**
 * [중복 확인] 결과 상태. `email` = **확인한 주소**(정규화) — 입력이 바뀌면 이 값과 달라져 결과가 무효가 된다.
 * idle = 아직 한 번도 안 눌렀다.
 */
export type EmailCheckState =
  | { kind: 'idle' }
  | { kind: 'checking'; email: string }
  | { kind: 'available'; email: string }
  | { kind: 'taken'; email: string; mine: boolean }
  | { kind: 'error'; email: string; message: string }
  /** 서버가 확인 경로를 모른다(옛 서버 404) — 저장 허용, 판정은 저장 409. */
  | { kind: 'unsupported'; email: string };

/** 화면 표시 색조 — 문구 옆 색(ok 초록 · err 빨강 · 없음 = 보통 회색). */
export type EmailCheckTone = 'ok' | 'err' | 'muted';

/** 입력값 + 확인 상태 → 화면이 보일 것. */
export interface EmailCheckView {
  /** 보일 문구(빈 문자열 = 아무것도 안 보인다). */
  message: string;
  tone: EmailCheckTone;
  /** 형식이 맞는다. */
  formatOk: boolean;
  /** **지금 입력값**이 확인을 통과했다 = 저장해도 된다. */
  confirmed: boolean;
  /**
   * [중복 확인] 버튼을 누를 수 있다 — 값이 있고 · 확인 중이 아니고 · 이 값의 결과가 아직 없을 때.
   * 형식이 틀려도 눌린다(누르면 형식 문구만 띄우고 서버에는 묻지 않는다 — `useEmailCheck.check`).
   */
  canCheck: boolean;
}

/**
 * 입력값과 확인 상태로 화면 표시를 정한다(순수 함수 — 시험 대상).
 *
 * @param value   입력칸의 지금 값
 * @param state   마지막 [중복 확인] 결과
 * @param touched 칸을 한 번 떠났거나 확인을 시도했다 — 그 전에는 형식 오류를 **치는 도중에** 띄우지 않는다
 *                ("a" 한 글자 쳤는데 빨간 글씨가 뜨면 성가시다).
 */
export function emailCheckView(value: string, state: EmailCheckState, touched: boolean): EmailCheckView {
  const email = normalizeEmail(value);
  const formatOk = isEmailFormat(value);
  // 결과가 **지금 값**에 대한 것인가 — 확인 뒤 한 글자라도 고치면 옛 결과는 무효.
  const current = state.kind !== 'idle' && state.email === email;

  if (!email) return { message: '', tone: 'muted', formatOk, confirmed: false, canCheck: false };
  if (!formatOk) {
    // 버튼은 **눌리게** 둔다 — 누르면 서버에 묻지 않고 형식 문구를 띄운다(꺼 두면 "왜 안 눌리지?" 가 된다).
    return { message: touched ? EMAIL_FORMAT_MESSAGE : '', tone: 'err', formatOk, confirmed: false, canCheck: true };
  }
  if (current) {
    switch (state.kind) {
      case 'checking':
        return { message: EMAIL_CHECKING_MESSAGE, tone: 'muted', formatOk, confirmed: false, canCheck: false };
      case 'available':
        return { message: EMAIL_AVAILABLE_MESSAGE, tone: 'ok', formatOk, confirmed: true, canCheck: false };
      case 'taken':
        return {
          message: state.mine ? EMAIL_MINE_MESSAGE : EMAIL_TAKEN_MESSAGE,
          tone: 'err',
          formatOk,
          confirmed: false,
          canCheck: false,
        };
      case 'error':
        // 네트워크·레이트리밋 등 — 다시 누를 수 있게 둔다.
        return { message: state.message, tone: 'err', formatOk, confirmed: false, canCheck: true };
      case 'unsupported':
        return { message: EMAIL_UNSUPPORTED_MESSAGE, tone: 'muted', formatOk, confirmed: true, canCheck: false };
    }
  }
  return { message: EMAIL_UNCHECKED_MESSAGE, tone: 'muted', formatOk, confirmed: false, canCheck: true };
}
